import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  SvgIconProps,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Column } from "../../../redux/slice/commonSlice";
import { tokens } from "../../../theme";
import Card from "../../common/Card";
import CustomTable from "../../common/CustomTable";
import {
  fetchInvoiceAgency,
  getInvoiceById,
  InvoiceDetailDTO,
  Payment,
  resetinvoiceAgencyList,
  useInvoiceSlice,
} from "../../../redux/slice/invoice/InvoiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useLocation } from "react-router-dom";
import {
  getPaymentsByInvoice,
  resetPaymentsByInvoiceId,
  usePaymentSlice,
} from "../../../redux/slice/payment/paymentSlice";
import { editCommissionRateAction } from "../../../redux/slice/order/OrderSlice";
import Button from "../../common/Button";
import PageBackButton from "../../common/PageBackButton";
import {
  AssessmentOutlined,
  CalendarTodayOutlined,
  Cancel,
  DescriptionOutlined,
  Edit,
  PaidOutlined,
  ReceiptLongOutlined,
  ReportGmailerrorred,
  ReportGmailerrorredOutlined,
  History,
  FileDownloadOutlined,
  ShoppingCartOutlined,
} from "@mui/icons-material";
import { useNavigation } from "../../../hooks/useNavigation";
import { selectIsTokenAvailable, selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { COMMISSION_STATUS, COMMISSION_TYPE_AGENCY_KEY, COMMISSION_TYPE_CODETABLE, COMMISSION_TYPE_V3_KEY, PAYMENT_STATUS, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import ExcelDetailsPopup from "../orders/ExcelDetailsPopup";
import {
  addCommission,
  AddCommissionRequest,
  getSalesDetailsByInvoiceId,
  getSalesDetailsByOrderId,
  updateCommission,
  UpdateCommissionDateRequest,
  useSaleDetailsSlice,
} from "../../../redux/slice/saleDetails/SaleDetailsSlice";
import CustomMultipleTable from "../../common/CustomMultipleTable";
import InvoiceCommissionSummary, {
  BreakdownPart,
  InvoiceCommissionRow,
} from "./InvoiceCommissionSummary";
import { useProSidebar } from "react-pro-sidebar";
import CommissionPayoutPopup from "./CommissionPayoutPopup";
import InfoPopup from "../../common/InfoPopup";
import { GridCheckCircleIcon } from "@mui/x-data-grid";
import DateUpdatePopup from "./DateUpdatePopup";
import EditCommissionPopup from "../orders/EditCommissionPopup";
import { getCodeTableDisplayLabel, isAgencyCommissionType, isOverrideCommissionType, isV3CommissionType, priceFormatter, toCommissionTypeRequestKey } from "../../../utils/Utility";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import CustomIconButton from "../../common/CustomIconButton";
import CustomAlert from "../../common/CustomAlert";
import { fetchAuditLogList } from "../../../redux/slice/auditLog/auditLogSlice";
import AuditLogDisplayPopup, { AuditLogData } from "../practice/AuditLogDisplayPopup";
import CommissionProfitSection, { deriveCommissionProfitView } from "./CommissionProfitSection";
import SectionTabNav, { SectionTabItem } from "../../common/SectionTabNav";
import PurchaseAndBillIndicator from "../../common/PurchaseAndBillIndicator";
import BillingDetail from "../invoice/BillingDetail";
import OrderDetails from "../orders/Details";
import {
  fetchCommissionProfitDetail,
  resetCommissionProfitDetail,
  useCommissionDetailSlice,
} from "../../../redux/slice/commission/commissionSlice";
import { exportCommissionDetailsToExcel } from "../../../utils/exportCommissionDetailsToExcel";
import { useModalStyles } from "../../common/useModalStyles";

const initializeAddCommissionRequest = (): AddCommissionRequest => {
  return {
    agencyId: 0,
    commissionAgencyType: "",
    invoiceId: undefined,
    commissionAmount: 0,
    commissionReceivedAmount: 0,
    v3CommissionReceivedAmount: undefined,
    payoutDate: "",
    payoutComment: undefined,
    paymentId: undefined,
  };
};

interface UpdateDateRequest {
  paymentId: string;
  level: string;
  invoiceId: number;
  agencyId: number;
  dateType: 'received' | 'paid';
  date: string;
}

interface PaymentWithShipping extends Payment {
  adjustedAmount: number;
  hasShippingDeduction: boolean;
  shippingChargeDeducted: number;
}

const parseCommissionRate = (commission: string) =>
  parseFloat(String(commission).replace("%", "")) || 0;

const roundCurrency = (amount: number) => Math.round(amount * 100) / 100;

const hasMixedRates = (rates: number[]) =>
  rates.length > 1 && rates.some((rate) => rate !== rates[0]);

type AgencyPaymentPart = {
  paymentIndex: number;
  rate: number;
  amount: number;
};

const receivedColumn: Column[] = [
  { id: "invoiceLevel", label: "Service Fee Type", minWidth: 60 },
  { id: "commission", label: "Service Fee", minWidth: 60, align: "right" },
  { id: "expected", label: "Receivable", minWidth: 60, align: "right" },
  { id: "expectedDate", label: "Receivable On", minWidth: 60, align: "right" },
  { id: "received", label: "Received", minWidth: 60, align: "right" },
  { id: "receivedDate", label: "Received Date", minWidth: 60, align: "right" },
  { id: "action", label: "Action", minWidth: 60, align: "center" },
];
const paidColumn: Column[] = [
  { id: "invoiceLevel", label: "Service Fee Type", minWidth: 60 },
  { id: "commission", label: "Service Fee", minWidth: 60, align: "right" },
  { id: "expected", label: "Payable", minWidth: 60, align: "right" },
  { id: "expectedDate", label: "Payable On", minWidth: 60, align: "right" },
  { id: "paid", label: "Paid", minWidth: 60, align: "right" },
  { id: "paidDate", label: "Paid Date", minWidth: 60, align: "right" },
  { id: "action", label: "Action", minWidth: 60, align: "center" },
];

const CommissionDetails: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const colors = tokens(theme.palette.mode);
  const { tokens: modalTokens } = useModalStyles();
  const dispatch = useAppDispatch();
  const location = useLocation();
  
  const routeState = useMemo(() => {
    if (location?.state) return location.state;
    const params = new URLSearchParams(location?.search ?? "");
    const queryInvoiceId = params.get("invoiceId");
    return {
      invoiceId: queryInvoiceId ? Number(queryInvoiceId) : undefined,
      orderNumber: params.get("orderNumber") ?? undefined,
      invStatus: params.get("invStatus") ?? undefined,
    };
  }, [location?.state, location?.search]);
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const { invoiceAgencyList } = useInvoiceSlice();

  // Stored agency types ("V3") are shown using their codetable displayValue
  // ("Primary Org"). V3 request keys come from the table so V3/v3 both resolve.
  const commissionTypeLabel = useCallback(
    (agencyType: string | null | undefined) =>
      getCodeTableDisplayLabel(
        allCodeTablesValuesMap,
        COMMISSION_TYPE_CODETABLE,
        agencyType
      ),
    [allCodeTablesValuesMap]
  );
  const isV3Type = useCallback(
    (value: string | null | undefined) =>
      isV3CommissionType(allCodeTablesValuesMap, value),
    [allCodeTablesValuesMap]
  );
  const isAgencyType = useCallback(
    (value: string | null | undefined) =>
      isAgencyCommissionType(allCodeTablesValuesMap, value),
    [allCodeTablesValuesMap]
  );
  const isOverrideType = useCallback(
    (value: string | null | undefined) =>
      isOverrideCommissionType(allCodeTablesValuesMap, value),
    [allCodeTablesValuesMap]
  );
  const [invoiceTotal, setInvoiceTotal] = useState<number>(0);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [orderId, setOrderId] = useState<number>(0);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [invoiceDetail, setInvoiceDetail] = useState<InvoiceDetailDTO | null>(null);
  const [creditAmount , setCreditAmount] = useState<number>(0);
  const { paymentsByInvoiceId } = usePaymentSlice();

  const { navigate } = useNavigation();
  const { userInfo } = useAuthSlice();
  const isAdminUser =
    userInfo?.userRole === USER_ROLES.ROLE_ADMIN ||
    userInfo?.userRole === USER_ROLES.ROLE_SUPERADMIN;
  const [expandedPanels, setExpandedPanels] = React.useState<string[]>([]);
  const [headerExpanded, setHeaderExpanded] = useState(true);
  /** Finance HQ details tabs — Invoice/Order reuse the real BillingDetail / Order Details components. */
  const [activeTab, setActiveTab] = useState<"summary" | "invoice" | "order">("summary");
  const [showExcelPopup, setShowExcelPopup] = useState(false);
  const { SalesDetailsInfo } = useSaleDetailsSlice();
  const { commissionProfitDetail } = useCommissionDetailSlice();

  const [v3Status, setV3Status] = useState<"fully received" | "partially received" | "not received" | "no commission">();
  const [agencyStatus, setAgencyStatus] = useState<"fully paid" | "partially paid" | "not paid" | "no commission">();
  const [ovrStatus, setOvrStatus] = useState<"fully paid" | "partially paid" | "not paid" | "no commission">();

  const [statusStr, setStatusStr] = useState<string>("");
  const [statusColor, setStatusColor] = useState<SvgIconProps["color"]>("inherit");
  const [bgColor, setBgColor] = useState<string>("");

  const { collapsed, toggled, broken } = useProSidebar();
  const isSidebarOpen = broken ? toggled : !collapsed;
  const [showCommissionPayoutPopup, setShowCommissionPayoutPopup] =
    React.useState<{
      commissionObject: AddCommissionRequest;
      title: string;
      label: string;
      show: boolean;
      expectedAmount?: number;
      alreadyPaidAmount?: number;
    }>({
      commissionObject: initializeAddCommissionRequest(),
      title: "",
      label: "",
      show: false,
    });
  const [showInfoPopup, setShowInfoPopup] = useState<{
    message: string;
    title: string;
    show: boolean;
  }>({ message: "", title: "", show: false });
  const [pendingCommissionData, setPendingCommissionData] = useState<{
    level: string;
    paymentId: string;
    amountToPay: number;
    expected: number;
    alreadyPaid: number;
    invoiceId: number;
    agencyId: number;
  } | null>(null);
  const [showDateUpdatePopup, setShowDateUpdatePopup] = useState<{
    show: boolean;
    data: UpdateDateRequest | null
  }>({ show: false, data: null });
  const [showEditCommissionPopup, setShowEditCommissionPopup] = useState<{
    show: boolean;
    paymentData: {
      paymentId: number;
      invoiceAgencyId: number;
      currentRate: number;
    } | null;
    agencyData: any;
  }>({ show: false, paymentData: null, agencyData: null });
  const [showNoChangeAlert, setShowNoChangeAlert] = useState(false);
  const [shippingCharge, setShippingCharge] = useState<number>(0);
  const canViewExcelData = useSelector((state: RootState) => selectIsTokenAvailable("canViewExcelData")(state));
  // const canEditCommissionRate = useSelector((state: RootState) => selectIsTokenAvailable("canEditCommissionRate")(state));
  const canEditCommissionRate = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessCommissionActions", COMMISSION_STATUS.EDIT)(state));

  const [lateFees, setLateFees] = useState<number>(0);

  // Audit log states
  const [showAuditLogPopup, setShowAuditLogPopup] = useState(false);
  const [auditLogData, setAuditLogData] = useState<AuditLogData[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  /**
   * Client-side fallback when the payment API does not return an adjustment
   * breakdown. `invoiceAmount` must already be the commissionable base
   * (invoice amount − discount); shipping / late fees are never commissionable.
   */
  const calculatePaymentsWithShippingDeduction = (
    payments: any[],
    invoiceAmount: number,
    shippingCharge: number,
    creditMemoTotal: number,
    lateFees: number
  ): PaymentWithShipping[] => {

    const effectiveInvoice = Math.max(invoiceAmount - (creditMemoTotal || 0), 0);

    if (!payments?.length || (shippingCharge <= 0 && lateFees <= 0)) {
      return payments?.map(payment => ({
        ...payment,
        adjustedAmount: payment.amount,
        hasShippingDeduction: false,
        shippingChargeDeducted: 0,
        hasLateFeeDeduction: false,
        lateFeeDeducted: 0
      })) || [];
    }

    let cumulativeAmount = 0;
    let shippingDeducted = 0;
    let lateFeeDeducted = 0;

    return payments.map((payment) => {
      const currentPaymentAmount = payment.amount || 0;
      let adjustedAmount = currentPaymentAmount;
      let hasShippingDeduction = false;
      let shippingChargeDeducted = 0;
      let hasLateFeeDeduction = false;
      let lateFeeDeductedAmount = 0;

      // Check if this payment completes or exceeds the commissionable invoice base
      if (cumulativeAmount + currentPaymentAmount > effectiveInvoice) {
        const excessAmount = (cumulativeAmount + currentPaymentAmount) - effectiveInvoice;

        // Cap excess amount to current payment (can't deduct more than payment itself)
        let maxDeductible = Math.min(excessAmount, currentPaymentAmount);

        // First, deduct shipping if not fully deducted
        if (shippingDeducted < shippingCharge) {
          const remainingShipping = shippingCharge - shippingDeducted;
          shippingChargeDeducted = Math.min(maxDeductible, remainingShipping);
          shippingChargeDeducted = Math.round(shippingChargeDeducted * 100) / 100;
          hasShippingDeduction = shippingChargeDeducted > 0;
          shippingDeducted += shippingChargeDeducted;
          maxDeductible -= shippingChargeDeducted;
        }

        // Then, deduct late fees if shipping is fully deducted and there's remaining deductible amount
        if (maxDeductible > 0 && lateFeeDeducted < lateFees) {
          const remainingLateFee = lateFees - lateFeeDeducted;
          lateFeeDeductedAmount = Math.min(maxDeductible, remainingLateFee);
          lateFeeDeductedAmount = Math.round(lateFeeDeductedAmount * 100) / 100;
          hasLateFeeDeduction = lateFeeDeductedAmount > 0;
          lateFeeDeducted += lateFeeDeductedAmount;
        }

        adjustedAmount = Math.max(0, currentPaymentAmount - shippingChargeDeducted - lateFeeDeductedAmount);
      }

      cumulativeAmount += currentPaymentAmount;

      return {
        ...payment,
        adjustedAmount,
        hasShippingDeduction,
        shippingChargeDeducted,
        hasLateFeeDeduction,
        lateFeeDeducted: lateFeeDeductedAmount
      };
    });
  };

  const paymentsWithShippingAdjustment: any = useMemo(() => {
    const apiPayments = paymentsByInvoiceId?.payments ?? [];

    const mapPaymentLevels = (payment: (typeof apiPayments)[number]) =>
      (payment.commissions ?? []).map((commission) => ({
        level: commission.agencyType || "",
        commission: `${commission.expectedCommissionRate || 0}%`,
        expected: commission.expectedCommissionAmount || 0,
        paid: commission.calculatedCommissionAmount || 0,
        expectedDate: commission.expectedDate || "",
        invoiceId: commission.invoiceId,
        agencyId: commission.agencyId,
        agencyName: commission.agencyName,
        commissionReceivedDate: commission.commissionReceivedDate,
      }));

    // Prefer backend-provided deduction breakdown when present. This is the
    // authoritative source for discounted invoices (commission base ≠ invoice
    // amount), where client-side excess math would otherwise mis-split shipping.
    if (
      apiPayments.length > 0 &&
      apiPayments.every((payment) => payment.adjustedAmount != null)
    ) {
      return apiPayments.map((payment) => ({
        id: payment.paymentId.toString().padStart(4, "0"),
        amount: payment.paymentAmount || 0,
        paymentDate: payment.paymentDate || "",
        paymentStatus: payment.paymentStatus,
        adjustedAmount: Number(payment.adjustedAmount) || 0,
        hasShippingDeduction: Boolean(payment.hasShippingDeduction),
        shippingChargeDeducted: Number(payment.shippingChargeDeducted) || 0,
        hasLateFeeDeduction: Boolean(payment.hasLateFeeDeduction),
        lateFeeDeducted: Number(payment.lateFeeDeducted) || 0,
        levels: mapPaymentLevels(payment),
      }));
    }

    // Fallback: derive the breakdown client-side using the commissionable base
    // (invoice amount − discount). Shipping / late fees are never commissionable.
    const discount = Number(invoiceDetail?.discountAmount ?? 0);
    const commissionableInvoiceAmount = Math.max(
      0,
      Number(invoiceTotal) - discount
    );

    const basePayments = apiPayments.map((payment) => ({
      id: payment.paymentId.toString().padStart(4, "0"),
      amount: payment.paymentAmount || 0,
      paymentDate: payment.paymentDate || "",
      paymentStatus: payment.paymentStatus,
      levels: mapPaymentLevels(payment),
    }));

    return calculatePaymentsWithShippingDeduction(
      basePayments,
      commissionableInvoiceAmount,
      shippingCharge,
      creditAmount,
      lateFees
    );
  }, [
    paymentsByInvoiceId?.payments,
    invoiceTotal,
    shippingCharge,
    creditAmount,
    lateFees,
    invoiceDetail?.discountAmount,
  ]);

  const totalV3ReceivedAdjusted = useMemo(() => {
    return paymentsWithShippingAdjustment.reduce((total: number, payment: any) => {
      const v3Level = payment.levels.find((level: any) => isV3Type(level.level));
      const v3Amount = v3Level?.paid || 0;

      // if (payment.hasShippingDeduction && payment.amount > 0) {
      //   const adjustmentRatio = payment.adjustedAmount / payment.amount;
      //   return total + (v3Amount * adjustmentRatio);
      // }

      const totalAdjusted = total + v3Amount;
      const roundedVal = Math.round(totalAdjusted * 100)/100;

      return roundedVal;
    }, 0);
  }, [paymentsWithShippingAdjustment]);

  const totalAgencyPaidAdjusted = useMemo(() => {
    return paymentsWithShippingAdjustment.reduce((total: number, payment: any) => {
      const agencyLevel = payment.levels.find((level: any) => level.level === "AGENCY");
      const agencyAmount = agencyLevel?.paid || 0;

      // if (payment.hasShippingDeduction && payment.amount > 0) {
      //   const adjustmentRatio = payment.adjustedAmount / payment.amount;
      //   return total + (agencyAmount * adjustmentRatio);
      // }

      const totalAdjusted = total + agencyAmount;
      const roundedVal = Math.round(totalAdjusted * 100)/100;

      return roundedVal;
    }, 0);
  }, [paymentsWithShippingAdjustment]);

  // const totalOverridePaid = useMemo(() => {
  //   return payments.reduce((total, payment) => {
  //     const overrideLevel = payment.levels.find(level => level.level === 'OVERRIDE');
  //     return total + (overrideLevel?.paid || 0);
  //   }, 0);
  // }, [payments]);

  const getTotalOverridePaidAdjusted = useMemo(() => {
    return (agencyId: number) => {
      return paymentsWithShippingAdjustment.reduce((total: number, payment: any) => {
        const overrideLevel = payment.levels.find(
          (level: any) => level.level === "OVERRIDE" && level.agencyId === agencyId
        );
        const overrideAmount = overrideLevel?.paid || 0;

        // Apply proportional reduction based on shipping adjustment
        // if (payment.hasShippingDeduction && payment.amount > 0) {
        //   const adjustmentRatio = payment.adjustedAmount / payment.amount;
        //   return total + (overrideAmount * adjustmentRatio);
        // }

        const totalAdjusted = total + overrideAmount;
        const roundedVal = Math.round(totalAdjusted * 100) / 100;

        return roundedVal;
      }, 0);
    };
  }, [paymentsWithShippingAdjustment]);

  const invoiceCommissionSummary = useMemo(() => {
    const activePayments = paymentsWithShippingAdjustment.filter(
      (payment: any) => payment.paymentStatus !== PAYMENT_STATUS.VOID
    );

    // Commission is earned only on the invoice value net of credits and upfront
    // discount; shipping and late fees are collected on top and never earn
    // commission. Each payment's `adjustedAmount` is the commission-earning
    // portion after those charges have been deducted, so the still-unpaid
    // commissionable balance is the commissionable base minus the adjusted
    // amounts already received.
    const discount = Number(invoiceDetail?.discountAmount ?? 0);
    const commissionableBase = roundCurrency(
      Math.max(0, Number(invoiceTotal) - discount - Number(creditAmount))
    );
    const totalCommissionablePaid = roundCurrency(
      activePayments.reduce(
        (sum: number, payment: any) =>
          sum + (payment.adjustedAmount ?? payment.amount ?? 0),
        0
      )
    );
    const remainingInvoiceAmount = roundCurrency(
      Math.max(0, commissionableBase - totalCommissionablePaid)
    );

    const buildAgencyPaymentParts = (
      agencyType: string,
      agencyId: number | undefined,
      field: "expected" | "received"
    ): AgencyPaymentPart[] =>
      activePayments.flatMap((payment: any, index: number): AgencyPaymentPart[] => {
        const level = payment.levels.find(
          (l: any) =>
            (l.level === agencyType || (isV3Type(agencyType) && isV3Type(l.level))) &&
            (agencyId === undefined || l.agencyId === agencyId)
        );
        if (!level) return [];

        return [
          {
            paymentIndex: index,
            rate: parseCommissionRate(level.commission),
            amount: field === "expected" ? level.expected || 0 : level.paid || 0,
          },
        ];
      });

    // Rate breakdown is payment-only (P1, P2, …). Remaining-invoice rates are
    // never shown — users only need the unpaid/pending amount under Receivable.
    const buildRateBreakdown = (
      paymentParts: AgencyPaymentPart[]
    ): BreakdownPart[] =>
      paymentParts.map((part) => ({
        label: `P${part.paymentIndex + 1}`,
        rate: part.rate,
      }));

    const buildAmountBreakdown = (
      paymentParts: AgencyPaymentPart[],
      expectedTotal: number,
      includeRemainder: boolean,
      remainderLabel: string = "Unpaid"
    ): BreakdownPart[] => {
      const parts: BreakdownPart[] = paymentParts.map((part) => ({
        label: `P${part.paymentIndex + 1}`,
        amount: part.amount,
      }));

      if (includeRemainder) {
        const paymentSum = roundCurrency(
          paymentParts.reduce((sum, part) => sum + part.amount, 0)
        );
        const remainderAmount = roundCurrency(expectedTotal - paymentSum);
        if (remainderAmount > 0.009) {
          parts.push({ label: remainderLabel, amount: remainderAmount });
        }
      }

      return parts;
    };

    const sumPaymentAmounts = (paymentParts: AgencyPaymentPart[]) =>
      roundCurrency(paymentParts.reduce((sum, part) => sum + part.amount, 0));

    // Invoice-level Receivable/Payable is the commission accrued across the
    // received payments (each payment's expected amount is already net of any
    // shipping / late-fee deductions) plus the commission still expected on the
    // portion of the invoice not yet paid. Deriving it from the payments keeps
    // the total consistent with the per-payment breakdown even when rates were
    // edited payment-by-payment.
    const computeExpectedTotal = (
      paymentParts: AgencyPaymentPart[],
      invoiceRate: number
    ) => {
      const paymentSum = sumPaymentAmounts(paymentParts);
      const unpaidCommission =
        remainingInvoiceAmount > 0
          ? roundCurrency((remainingInvoiceAmount * invoiceRate) / 100)
          : 0;
      return roundCurrency(paymentSum + unpaidCommission);
    };

    const buildAgencyRow = (
      key: string,
      label: string,
      invoiceRate: number,
      expectedTotal: number,
      receivedTotal: number,
      paymentParts: AgencyPaymentPart[],
      options?: {
        alwaysShowRateBreakdown?: boolean;
        rateBreakdownOnly?: boolean;
        showExpectedBreakdown?: boolean;
        /** Label for the remaining-invoice amount part (V3 uses "Pending"). */
        remainderLabel?: string;
      }
    ): InvoiceCommissionRow => {
      const paymentRates = paymentParts.map((part) => part.rate);
      const isEdited = hasMixedRates(paymentRates);
      const includeRemainder = remainingInvoiceAmount > 0;
      const showRateBreakdown =
        options?.alwaysShowRateBreakdown || (isEdited && paymentParts.length > 0);
      const showExpectedBreakdown =
        options?.showExpectedBreakdown ?? isEdited;

      return {
        key,
        label,
        rate: invoiceRate,
        rateBreakdown: showRateBreakdown
          ? buildRateBreakdown(paymentParts)
          : undefined,
        rateBreakdownOnly:
          showRateBreakdown && (options?.rateBreakdownOnly ?? isEdited),
        expectedTotal,
        expectedBreakdown: showExpectedBreakdown
          ? buildAmountBreakdown(
              paymentParts,
              expectedTotal,
              includeRemainder,
              options?.remainderLabel ?? "Unpaid"
            )
          : undefined,
        receivedTotal,
      };
    };

    const v3Rows = (invoiceAgencyList ?? [])
      .filter((item) => isV3Type(item.agencyType))
      .map((lvl) => {
        const paymentParts = buildAgencyPaymentParts(COMMISSION_TYPE_V3_KEY, lvl.agencyId, "expected");
        return buildAgencyRow(
          `v3-${lvl.agencyId}`,
          commissionTypeLabel(lvl.agencyType),
          lvl.expectedCommissionRate,
          computeExpectedTotal(paymentParts, lvl.expectedCommissionRate),
          totalV3ReceivedAdjusted,
          paymentParts,
          { remainderLabel: "Pending" }
        );
      });

    const payableRows = (invoiceAgencyList ?? [])
      .filter((item) => !isV3Type(item.agencyType))
      .map((lvl) => {
        const paymentParts = buildAgencyPaymentParts(
          lvl.agencyType,
          lvl.agencyId,
          "expected"
        );
        const receivedTotal =
          lvl.agencyType === "AGENCY"
            ? totalAgencyPaidAdjusted
            : lvl.agencyType === "OVERRIDE"
              ? getTotalOverridePaidAdjusted(lvl.agencyId)
              : 0;

        return buildAgencyRow(
          `${lvl.agencyType}-${lvl.agencyId}`,
          `${commissionTypeLabel(lvl.agencyType)} (${lvl.agencyName})`,
          lvl.expectedCommissionRate,
          computeExpectedTotal(paymentParts, lvl.expectedCommissionRate),
          receivedTotal,
          paymentParts
        );
      });

    const otherAgencies = (invoiceAgencyList ?? []).filter(
      (item) => !isV3Type(item.agencyType)
    );
    const totalOtherCommissionRate = otherAgencies.reduce(
      (sum, item) => sum + (item.expectedCommissionRate || 0),
      0
    );
    const totalOtherPaid = otherAgencies.reduce((sum, item) => {
      if (item.agencyType === "AGENCY") {
        return sum + totalAgencyPaidAdjusted;
      }
      if (item.agencyType === "OVERRIDE") {
        return sum + getTotalOverridePaidAdjusted(item.agencyId);
      }
      return sum;
    }, 0);

    const netRows = (invoiceAgencyList ?? [])
      .filter((item) => isV3Type(item.agencyType))
      .map((lvl) => {
        const netPaymentParts: AgencyPaymentPart[] = [];

        activePayments.forEach((payment: any, index: number) => {
          const v3Level = payment.levels.find((level: any) => isV3Type(level.level));
          if (!v3Level) return;

          const paymentOtherLevels = payment.levels.filter(
            (level: any) => !isV3Type(level.level)
          );
          const otherRate = paymentOtherLevels.reduce(
            (sum: number, level: any) => sum + parseCommissionRate(level.commission),
            0
          );
          const otherExpected = paymentOtherLevels.reduce(
            (sum: number, level: any) => sum + (level.expected || 0),
            0
          );
          const v3Rate = parseCommissionRate(v3Level.commission);
          const netRate = v3Rate - otherRate;

          netPaymentParts.push({
            paymentIndex: index,
            rate: netRate,
            amount: v3Level.expected - otherExpected,
          });
        });

        const netRate = lvl.expectedCommissionRate - totalOtherCommissionRate;
        const netPaymentSum = roundCurrency(
          netPaymentParts.reduce((sum, part) => sum + part.amount, 0)
        );
        const netUnpaidCommission =
          remainingInvoiceAmount > 0
            ? roundCurrency((remainingInvoiceAmount * netRate) / 100)
            : 0;
        const netExpectedTotal = roundCurrency(
          netPaymentSum + netUnpaidCommission
        );
        const showNetRateBreakdown = netPaymentParts.length > 1;

        return {
          key: `net-${lvl.agencyId}`,
          label: commissionTypeLabel(lvl.agencyType),
          rate: netRate,
          rateBreakdown: showNetRateBreakdown
            ? buildRateBreakdown(netPaymentParts)
            : undefined,
          rateBreakdownOnly: showNetRateBreakdown,
          expectedTotal: netExpectedTotal,
          receivedTotal: totalV3ReceivedAdjusted - totalOtherPaid,
        };
      });

    return { v3Rows, payableRows, netRows };
  }, [
    invoiceAgencyList,
    totalV3ReceivedAdjusted,
    totalAgencyPaidAdjusted,
    getTotalOverridePaidAdjusted,
    paymentsWithShippingAdjustment,
    invoiceTotal,
    shippingCharge,
    lateFees,
    creditAmount,
    invoiceDetail?.discountAmount,
    commissionTypeLabel,
  ]);

  useEffect(() => {
    const v3Item = invoiceAgencyList?.find((a) => isV3Type(a.agencyType));

    if (!v3Item) {
      setV3Status(undefined);
    } else {
      const v3Expected = v3Item.expectedCommissionAmount ?? 0;
      const v3ExpectedRate = v3Item.expectedCommissionRate ?? 0;

      if (v3ExpectedRate === 0) {
        setV3Status("no commission");
      } else if (totalV3ReceivedAdjusted >= v3Expected) {
        setV3Status("fully received");
      } else if (totalV3ReceivedAdjusted > 0) {
        setV3Status("partially received");
      } else {
        setV3Status("not received");
      }
    }

    const agencyItem = invoiceAgencyList?.find((a) => a.agencyType === "AGENCY");
    if (agencyItem) {
      const agencyExpected = agencyItem.expectedCommissionAmount ?? 0;
      const agencyExpectedRate = agencyItem.expectedCommissionRate ?? 0;

      if (agencyExpectedRate === 0) {
        setAgencyStatus('no commission')
      } else {
        if (totalAgencyPaidAdjusted >= agencyExpected) {
          setAgencyStatus("fully paid");
        } else if (totalAgencyPaidAdjusted > 0) {
          setAgencyStatus("partially paid");
        } else {
          setAgencyStatus("not paid");
        }
      }
    } else {
      setAgencyStatus(undefined);
    }

    const overrides = invoiceAgencyList?.filter((a) => a.agencyType === "OVERRIDE") ?? [];
    if (overrides.length > 0) {
      const overrideExpected = overrides.reduce(
        (sum, a) => sum + a.expectedCommissionAmount,
        0
      );
      const overridePaid = overrides.reduce(
        (sum, a) => sum + getTotalOverridePaidAdjusted(a.agencyId),
        0
      );
      const overrideExpectedRate = overrides.reduce(
        (sum, a) => sum + a.expectedCommissionRate,
        0
      );

      if (overrideExpectedRate === 0) {
        setOvrStatus("no commission")
      } else {

        if (overridePaid >= overrideExpected) {
          setOvrStatus("fully paid");
        } else if (overridePaid > 0) {
          setOvrStatus("partially paid");
        } else {
          setOvrStatus("not paid");
        }
      }
    } else {
      setOvrStatus(undefined);
    }

  }, [invoiceAgencyList, totalV3ReceivedAdjusted, totalAgencyPaidAdjusted, getTotalOverridePaidAdjusted, routeState?.invStatus]);

  useEffect(() => {

    const invStatus = routeState?.invStatus;

    if (invStatus === "PAID") {
      const isV3Fully =
        v3Status === "fully received" ||
        v3Status === "no commission" ||
        v3Status === undefined;
      const isAgencyFully = agencyStatus === "fully paid" || agencyStatus === "no commission" || agencyStatus === undefined;
      const isOvrFully = ovrStatus === "fully paid" || ovrStatus === "no commission" || ovrStatus === undefined;

      if (isV3Fully && isAgencyFully && isOvrFully) {
        setStatusColor("success");
        setBgColor("rgba(17, 150, 4, 0.2)");
      } else if (
        isV3Fully &&
        (!isAgencyFully || !isOvrFully) &&
        (agencyStatus !== undefined || ovrStatus !== undefined)
      ) {
        setStatusColor("warning");
        setBgColor("rgba(148, 150, 4, 0.2)");
      } else if (!isV3Fully) {
        setStatusColor("inherit");
        setBgColor("rgba(255, 255, 255, 0.2)");
      }
    } else {
      setStatusColor("inherit");
      setBgColor("rgba(255, 255, 255, 0.2)");
    }

    let msgParts: string[] = [];

    if (invStatus) {
      if (invStatus === "PARTIAL") {
        msgParts.push(`Invoice is partially paid`);
      } else if (invStatus === "PAID") {
        msgParts.push(`Invoice is fully paid`);
      }
    }

    if (v3Status === "no commission") {
      msgParts.push("No V3 revenue");
    } else if (v3Status) {
      msgParts.push(`V3 revenue is ${v3Status}`);
    }

    if (agencyStatus) {
      if (agencyStatus === 'no commission') {
        msgParts.push(`No agency service fees`);
      } else {
        msgParts.push(`Agency service fee is ${agencyStatus}`);
      }
    }

    if (ovrStatus) {
      if (ovrStatus === 'no commission') {
        msgParts.push(`No override service fees`);
      } else {
        msgParts.push(`Override service fee is ${ovrStatus}`);
      }
    }

    setStatusStr(msgParts.join(". ") + ".");


  }, [v3Status, agencyStatus, ovrStatus, commissionTypeLabel])

  const loadFinanceHqSummary = useCallback(async (id: number | string) => {
    if (id === undefined || id === null || id === "") return;
    const numericId = typeof id === "string" ? parseInt(id, 10) : id;
    if (!numericId || Number.isNaN(numericId)) return;

    const response = await dispatch(getInvoiceById({ invoiceId: numericId, isSaveAuditLog: false }));
    const invoiceData = response.payload?.data;
    if (invoiceData) {
      setInvoiceTotal(invoiceData.invoiceAmount);
      setShippingCharge(invoiceData.shippingCharges);
      setInvoiceNumber(invoiceData.mfgInvoiceNumber);
      setOrderNumber(invoiceData.orderNumber);
      setOrderId(invoiceData.orderId);
      setInvoiceDate(invoiceData.mfgInvoiceDate);
      setInvoiceStatus(invoiceData.invoiceStatus || routeState?.invStatus || "");
      setInvoiceDetail(invoiceData);
      setCreditAmount(invoiceData.creditAmount);
      setLateFees(invoiceData.lateFee);
    }

    dispatch(getSalesDetailsByInvoiceId(numericId));
    if (isAdminUser) {
      dispatch(fetchCommissionProfitDetail(numericId));
    }
    dispatch(fetchInvoiceAgency(numericId));
    dispatch(getPaymentsByInvoice(numericId));
  }, [dispatch, isAdminUser, routeState?.invStatus]);

  // Mount
  useEffect(() => {
    const id = routeState?.invoiceId;
    setInvoiceId(id);

    if (routeState?.orderNumber) {
      setOrderNumber(routeState.orderNumber);
    }
    if (routeState?.invStatus) {
      setInvoiceStatus(routeState.invStatus);
    }

    if (id) {
      loadFinanceHqSummary(id);
    }
    // Initial load only — returning to Summary is handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPurchaseAndBill =
    invoiceDetail?.purchaseAndBill === "Y" ||
    !!commissionProfitDetail?.purchaseAndBillEnabled;

  const nonPbServiceFees = useMemo(() => {
    // Same totals the Service Fee Receivable / Payable cards show: each payment's
    // expected amount plus the unpaid balance. Invoice-level expected amounts
    // miss edited per-payment rates and credits.
    const sumExpected = (rows: InvoiceCommissionRow[]) =>
      roundCurrency(rows.reduce((total, row) => total + (Number(row.expectedTotal) || 0), 0));

    return {
      v3ServiceFeeTotal: sumExpected(invoiceCommissionSummary.v3Rows),
      agencyServiceFeeTotal: sumExpected(
        invoiceCommissionSummary.payableRows.filter((row) =>
          String(row.key).toUpperCase().startsWith("AGENCY-")
        )
      ),
      overrideServiceFeeTotal: sumExpected(
        invoiceCommissionSummary.payableRows.filter((row) =>
          String(row.key).toUpperCase().startsWith("OVERRIDE-")
        )
      ),
    };
  }, [invoiceCommissionSummary]);

  const showCommissionProfitSection =
    isAdminUser && !!invoiceDetail && (isPurchaseAndBill ? !!commissionProfitDetail : true);

  const detailTabs = useMemo<SectionTabItem[]>(
    () => [
      {
        id: "summary",
        label: "Summary",
        icon: <AssessmentOutlined fontSize="small" />,
      },
      {
        id: "invoice",
        label: invoiceNumber ? `Invoice #${invoiceNumber}` : "Invoice",
        icon: <ReceiptLongOutlined fontSize="small" />,
        disabled: !invoiceId,
      },
      {
        id: "order",
        label: orderNumber ? `Order #${orderNumber}` : "Order",
        icon: <ShoppingCartOutlined fontSize="small" />,
        disabled: !orderId,
      },
    ],
    [invoiceId, invoiceNumber, orderId, orderNumber]
  );

  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevTabRef.current !== "summary" && activeTab === "summary" && invoiceId) {
      loadFinanceHqSummary(invoiceId);
    }
    prevTabRef.current = activeTab;
  }, [activeTab, invoiceId, loadFinanceHqSummary]);

  //Unmount
  useEffect(
    () => () => {
      dispatch(resetinvoiceAgencyList());
      dispatch(resetPaymentsByInvoiceId());
      dispatch(resetCommissionProfitDetail());
    },
    []
  );

  const handleAccordionToggle =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanels((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((id) => id !== panel)
      );
    };

  /** Payments open by default; a panel the user closed stays closed on refresh. */
  const autoExpandedPaymentIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const newIds = paymentsWithShippingAdjustment
      .map((payment: any) => payment.id as string)
      .filter((id: string) => !autoExpandedPaymentIdsRef.current.has(id));

    if (newIds.length === 0) return;

    newIds.forEach((id: string) => autoExpandedPaymentIdsRef.current.add(id));
    setExpandedPanels((prev) => [...prev, ...newIds.filter((id: string) => !prev.includes(id))]);
  }, [paymentsWithShippingAdjustment]);

  const showExcelPopupData = async (event: React.MouseEvent<HTMLElement>) => {
    setShowExcelPopup(true)
  };

  const handleConfirmCommissionPayout = async (data: AddCommissionRequest) => {
    const response = await dispatch(addCommission(data));
    if (response.payload && response.payload.code === "V3OK") {
      dispatch(getPaymentsByInvoice(parseInt(invoiceId)));
    }
  };

  const handleUpdateCommissionDate = async (updatedRequest: UpdateDateRequest) => {
    const data: UpdateCommissionDateRequest = {
      agencyId: updatedRequest.agencyId,
      agencyType: updatedRequest.level,
      invoiceId: updatedRequest.invoiceId,
      commissionDate: updatedRequest.date,
      paymentId: parseInt(updatedRequest.paymentId)
    }
    const response = await dispatch(updateCommission(data));
    if (response.payload && response.payload.code === "V3OK") {
      dispatch(getPaymentsByInvoice(parseInt(invoiceId)));
    }
  }

  //CS-1657 START
  const handleCommissionRateUpdate = async (data: any) => {
    // Check if no change was made
    if (data.noChange) {
      setShowNoChangeAlert(true);
      return;
    }

    try {
      // Call the API to update commission rate
      const response = await dispatch(editCommissionRateAction(data));
      console.log("%c Line:686 🍓 response", "color:#4fff4B", response);

      if (response.payload && response.payload.data.code === "V3OK") {
        // Refresh both payments and invoice agency data after successful update
        dispatch(getPaymentsByInvoice(parseInt(invoiceId)));
        dispatch(fetchInvoiceAgency(parseInt(invoiceId)));
      }
    } catch (error) {
      console.error('Error updating commission rate:', error);
    }

    // Close the popup regardless of success/failure
    setShowEditCommissionPopup({ show: false, paymentData: null, agencyData: null });
  };

  const handleAuditLogClick = async () => {
    if (!invoiceNumber) return;

    setAuditLogLoading(true);
    try {
      // Fetch audit logs using invoiceNumber as entityNumber
      const response = await dispatch(fetchAuditLogList({
        entityName: "INVOICE",
        entityNumber: invoiceNumber,
        currentPage: 0,
        itemsPerPage: 100,
        actionType: ["MODIFY"],
        userName: null,
        fromDate: undefined,
        toDate: undefined
      }));

      if (response.payload && response.payload.data && response.payload.data.searchAuditData) {
        const auditData = response.payload.data.searchAuditData.content || [];

        // Transform the data to match AuditLogData interface
        const transformedData: AuditLogData[] = auditData.map((log: any) => ({
          entityNumber: log.entityNumber || invoiceNumber,
          entityName: log.entityName || "INVOICE",
          action: log.action || "",
          auditedObject: log.auditedObject,
          actionType: log.actionType || "",
          clientIP: log.clientIP || "",
          auditUser: log.auditUser,
          auditTimestamp: log.auditTimestamp || "",
          userName: log.userName || log.auditUser?.userName || "Unknown User"
        }));

        setAuditLogData(transformedData);
        setShowAuditLogPopup(true);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      // Show empty popup with error message
      setAuditLogData([]);
      setShowAuditLogPopup(true);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const handleCloseAuditLog = () => {
    setShowAuditLogPopup(false);
    setAuditLogData([]);
  };

  // Commission is calculated on invoice amount net of discount and credit.
  const discountAmount = Number(invoiceDetail?.discountAmount ?? 0);
  const commissionBaseAmount =
    Number(invoiceTotal) - discountAmount - Number(creditAmount);

  // Invoice total on the Invoice Calculation card: base + shipping + late fees.
  const invoiceGrandTotal =
    commissionBaseAmount + Number(shippingCharge) + Number(lateFees);

  const handleExportCommissionDetails = async () => {
    if (!invoiceNumber) {
      return;
    }

    const calculatedInvoiceTotal =
      Number(invoiceTotal) + Number(shippingCharge) + Number(lateFees) - Number(creditAmount);
    const profitView = deriveCommissionProfitView({
      purchaseAndBill: isPurchaseAndBill,
      cogsAmount: commissionProfitDetail?.cogsAmount ?? 0,
      shippingChargesActual: commissionProfitDetail?.shippingChargesActual ?? 0,
      invoiceTotal: isPurchaseAndBill
        ? (commissionProfitDetail?.invoiceTotal ?? calculatedInvoiceTotal)
        : invoiceGrandTotal,
      v3ServiceFeeTotal: nonPbServiceFees.v3ServiceFeeTotal,
      agencyCommissionAmount: isPurchaseAndBill
        ? (commissionProfitDetail?.agencyCommissionAmount ?? 0)
        : nonPbServiceFees.agencyServiceFeeTotal,
      overrideCommissionAmount: isPurchaseAndBill
        ? (commissionProfitDetail?.overrideCommissionAmount ?? 0)
        : nonPbServiceFees.overrideServiceFeeTotal,
    });

    const commissionRows = (invoiceAgencyList ?? [])
      .filter((item) => !isV3Type(item.agencyType))
      .map((item) => ({
        commissionType: `${commissionTypeLabel(item.agencyType)} (${item.agencyName})`,
        commissionPercent: `${item.expectedCommissionRate}%`,
        payable: item.expectedCommissionAmount ?? 0,
        paid:
          item.agencyType === "AGENCY"
            ? totalAgencyPaidAdjusted
            : item.agencyType === "OVERRIDE"
              ? getTotalOverridePaidAdjusted(item.agencyId)
              : 0,
      }));

    await exportCommissionDetailsToExcel({
      invoiceNumber,
      invoiceAmount: Number(invoiceTotal),
      shippingCharges: Number(shippingCharge),
      creditAmount: Number(creditAmount),
      lateFees: Number(lateFees),
      invoiceTotal: isPurchaseAndBill
        ? (commissionProfitDetail?.invoiceTotal ?? calculatedInvoiceTotal)
        : calculatedInvoiceTotal,
      profitInvoiceTotal: profitView.invoiceTotal,
      purchaseAndBillEnabled: isPurchaseAndBill,
      cogsAmount: profitView.cogsAmount,
      shippingChargesActual: commissionProfitDetail?.shippingChargesActual ?? 0,
      v3TotalCost: profitView.v3TotalCost,
      v3ServiceFeeTotal: profitView.v3ServiceFeeTotal,
      agencyCommissionAmount: profitView.agencyCommissionAmount,
      overrideCommissionAmount: profitView.overrideCommissionAmount,
      finalProfit: profitView.profit,
      commissionRows,
    });
  };
  //CS-1657 END

  const handleDateClick = ({
    date,
    paymentId,
    level,
    invoiceId,
    agencyId,
    dateType,
  }: {
    date: string;
    paymentId: string;
    level: string;
    invoiceId: number;
    agencyId: number;
    dateType: "received" | "paid";
  }) => {
    const updateRequest: UpdateDateRequest = {
      paymentId,
      level,
      invoiceId,
      agencyId,
      dateType,
      date,
    };

    setShowDateUpdatePopup({
      show: true,
      data: updateRequest
    });
  };

  const createCommissionObject = (
    level: string,
    amountToPay: number,
    paymentId: string,
    invoiceId: number,
    agencyId: number
  ): AddCommissionRequest => {
    const payment = paymentsWithShippingAdjustment.find((p: any) => p.id === paymentId);
    const v3Level = payment?.levels.find((l: any) => isV3Type(l.level));
    const v3ExpectedAmount = v3Level?.expected || 0;

    const baseCommission: AddCommissionRequest = {
      agencyId: agencyId,
      invoiceId: invoiceId,
      commissionAgencyType: toCommissionTypeRequestKey(allCodeTablesValuesMap, level),
      commissionAmount: amountToPay,
      payoutDate: "",
      paymentId: parseInt(paymentId),
      payoutComment: "",
      // Pay only the remaining unpaid commission balance
      commissionReceivedAmount: amountToPay,
      v3CommissionReceivedAmount: v3ExpectedAmount,
    };
    return baseCommission;
  };

  const getRemainingCommissionAmount = (expected: number, alreadyPaid: number) =>
    Math.max(0, Math.round((expected - alreadyPaid) * 100) / 100);

  const openCommissionPayoutPopup = (
    level: string,
    paymentId: string,
    invoiceId: number,
    agencyId: number,
    expected: number,
    alreadyPaid: number
  ) => {
    const amountToPay = getRemainingCommissionAmount(expected, alreadyPaid);
    if (amountToPay <= 0) return;

    setShowCommissionPayoutPopup({
      commissionObject: createCommissionObject(
        level,
        amountToPay,
        paymentId,
        invoiceId,
        agencyId
      ),
      title: isV3Type(level)
        ? "V3 Revenue"
        : isOverrideCommissionType(allCodeTablesValuesMap, level)
          ? "Override Service Fee Payout"
          : "Agency Service Fee Payout",
      label: isV3Type(level)
        ? "V3 Service Fee Amount"
        : isOverrideCommissionType(allCodeTablesValuesMap, level)
          ? "Override Service Fee Amt"
          : "Agency Service Fee Amt",
      show: true,
      expectedAmount: expected,
      alreadyPaidAmount: alreadyPaid,
    });
  };

  const renderActionButtons = (ctx: {
    paymentId: string;
    level: string;
    received?: number;
    paid?: number;
    expected: number;
    invoiceId: number;
    agencyId: number;
    commissionRate?: number;
    invoiceAgencyId?: number;
  }) => {
    // Check if V3 commission has been received for this payment
    const checkV3CommissionReceived = (paymentId: string): boolean => {
      const payment = paymentsWithShippingAdjustment.find((p: any) => p.id === paymentId);
      const v3Level = payment?.levels.find((l: any) => isV3Type(l.level));
      // No V3 commission on this payment (e.g. P&B invoices) — nothing to wait for, skip the warning
      if (!v3Level || (v3Level.expected || 0) <= 0) {
        return true;
      }
      return (v3Level.paid || 0) > 0;
    };

    const alreadyPaidAmount =
      isV3Type(ctx.level) ? ctx.received || 0 : ctx.paid || 0;
    const remainingAmount = getRemainingCommissionAmount(
      ctx.expected,
      alreadyPaidAmount
    );

    const handleCommissionPayout = (level: string, paymentId: string) => {
      if (
        (isAgencyType(level) || isOverrideType(level)) &&
        !checkV3CommissionReceived(paymentId)
      ) {
        setPendingCommissionData({
          level,
          paymentId,
          amountToPay: remainingAmount,
          expected: ctx.expected,
          alreadyPaid: alreadyPaidAmount,
          invoiceId: ctx.invoiceId,
          agencyId: ctx.agencyId,
        });

        setShowInfoPopup({
          message: `V3 service fee has not been received yet for this payment. You can still proceed with the ${level.toLowerCase()} service fee payout by clicking 'Confirm' to continue.`,
          title: "Warning",
          show: true,
        });
        return;
      }

      openCommissionPayoutPopup(
        level,
        ctx.paymentId,
        ctx.invoiceId,
        ctx.agencyId,
        ctx.expected,
        alreadyPaidAmount
      );
    };

    //CS-1657 START
    const handleEditCommission = () => {
      const payment = paymentsWithShippingAdjustment.find((p: any) => p.id === ctx.paymentId);
      const levelData = payment?.levels.find((l: any) => l.level === ctx.level);

      setShowEditCommissionPopup({
        show: true,
        paymentData: {
          paymentId: parseInt(ctx.paymentId),
          invoiceAgencyId: ctx.invoiceAgencyId || 0,
          currentRate: ctx.commissionRate || 0
        },
        agencyData: {
          agencyName: levelData?.agencyName || `${commissionTypeLabel(ctx.level)} Agency`,
          invoiceAmt: invoiceTotal
        }
      });
    };

    // Check if edit button should be visible
    const shouldShowEditButton = () => {
      if (!canEditCommissionRate) return false;

      if (isV3Type(ctx.level)) {
        // Allow edit only when V3 commission is still unpaid
        return (ctx.received || 0) === 0 && ctx.expected > 0;
      }

      // Agency/Override: hide edit once any amount is paid (including partial payouts)
      return (ctx.paid || 0) === 0 && ctx.expected > 0;
    };
    //CS-1657 END

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Stack direction="row" spacing={0.5}>
          {isV3Type(ctx.level) && remainingAmount > 0 && (
            <Tooltip title="V3 Revenue">
              <IconButton
                size="small"
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCommissionPayout(ctx.level, ctx.paymentId);
                }}
              >
                <PaidOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isOverrideType(ctx.level) && remainingAmount > 0 && (
            <Tooltip title="Override Service Fee Payout">
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCommissionPayout(ctx.level, ctx.paymentId);
                }}
              >
                <PaidOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isAgencyType(ctx.level) && remainingAmount > 0 && (
            <Tooltip title="Agency Service Fee Payout">
              <IconButton
                size="small"
                color="warning"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCommissionPayout(ctx.level, ctx.paymentId);
                }}
              >
                <PaidOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {shouldShowEditButton() && (
        // <CustomIconButton
        //   color="info"
        //   variant="contained"
        //   size="small"
        //   tooltipMessage="Edit"
        //   tooltipPlacement="bottom"
        //   onClick={() => handleEditCommission()}
        // >
        //   <Edit fontSize="small" />
        // </CustomIconButton>
            <Tooltip title="Edit Service Rate">
              <IconButton
                size="small"
                color="info"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditCommission();
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

        </Stack>
      </Box>
    );
  };

  const money = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
    }).format(n);

  const getInvoiceStatusDisplay = (status: string) => {
    switch (status) {
      case "PARTIAL":
        return { label: "PARTIAL", color: "#D97706" };
      case "PAID":
        return { label: "PAID", color: "#28a745" };
      case "UNPAID":
        return { label: "UNPAID", color: "#dc3545" };
      case "PROCESSING":
        return { label: "PROCESSING", color: "#5dabff" };
      case "CLOSED":
        return { label: "CLOSED", color: "#6b7280" };
      default:
        return { label: status || "-", color: colors.grey[400] };
    }
  };

  const headerCardSx = {
    borderRadius: 3,
    backgroundColor: theme.palette.mode === "dark" ? "#03112146" : "#e4e4e454",
    border: `1px solid #6d6d6d46`,
  };

  const headerRowSx = {
    px: { xs: 1.5, sm: 2.5 },
    py: { xs: 2, sm: 2.5 },
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "stretch",
    gap: 2,
  };

  const headerMetaBadgeSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.5,
    py: 0.75,
    borderRadius: "999px",
    border: `1px solid ${colors.grey[700]}`,
    backgroundColor:
      theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.04)",
    width: "fit-content",
    maxWidth: "100%",
  };

  const headerMetaLabelSx = {
    color: colors.grey[300],
    fontSize: "0.75rem",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
  };

  const headerMetaValueSx = {
    color: colors.grey[100],
    fontSize: "0.75rem",
    lineHeight: 1.2,
    fontWeight: 600,
    whiteSpace: "nowrap",
  };

  const invoiceStatusDisplay = getInvoiceStatusDisplay(invoiceStatus);

  type PayState = "paid" | "partial" | "unpaid";

  interface Tile {
    key: string;
    label: string;
    state: PayState;
    paid: number;
    total: number;
  }

  const tiles: Tile[] = [
    {
      key: "invoice",
      label: "Invoice",
      state: "paid",
      paid: 125000,
      total: 125000,
    },
    {
      key: "v3",
      label: `${commissionTypeLabel(COMMISSION_TYPE_V3_KEY)} Service Fee`,
      state: "partial",
      paid: 15750,
      total: 31250,
    },
    {
      key: "agency",
      label: "Agency Service Fee",
      state: "unpaid",
      paid: 0,
      total: 18750,
    },
    { key: "ovr", label: "OVR Payout", state: "unpaid", paid: 0, total: 6250 },
  ];

  const stateMeta: Record<
    PayState,
    {
      label: string;
      color: "success" | "warning" | "error";
      bg: string;
      ring: string;
      icon: typeof SvgIcon;
    }
  > = {
    paid: {
      label: "Fully Paid",
      color: "success",
      bg: "rgba(64, 77, 64, 0.06)",
      ring: "rgba(76, 175, 80, 0.25)",
      icon: GridCheckCircleIcon,
    },
    partial: {
      label: "Partially Paid",
      color: "warning",
      bg: "rgba(255, 193, 7, 0.08)",
      ring: "rgba(255, 193, 7, 0.25)",
      icon: ReportGmailerrorredOutlined,
    },
    unpaid: {
      label: "Not Paid",
      color: "error",
      bg: "rgba(244, 67, 54, 0.06)",
      ring: "rgba(244, 67, 54, 0.25)",
      icon: Cancel,
    },
  };

  const StatusChip: React.FC<{ state: PayState }> = ({ state }) => (
    <Chip
      size="small"
      label={stateMeta[state].label}
      color={stateMeta[state].color}
      variant="outlined"
      sx={{
        fontWeight: 600,
        bgcolor: stateMeta[state].bg,
        borderColor: stateMeta[state].ring,
      }}
    />
  );
  const StateIcon: React.FC<{ state: PayState }> = ({ state }) => {
    const Icon = stateMeta[state].icon;
    return <Icon color={stateMeta[state].color} fontSize="small" />;
  };

  const lastUpdated = new Date("2024-12-12T14:45:00");

  const commExportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: isDark ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const commViewChangesBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#00B8D9",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const lineTextColor = isDark ? "#fff" : modalTokens.textPrimary;

  return (
    <Box m="15px">
      <Box mb={1.5} display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="flex-start" gap={1.5}>
          <PageBackButton />
          <Box>
            <Typography
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: modalTokens.textSecondary,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                mb: 0.3,
              }}
            >
              FinanceHQ
            </Typography>
            <Typography
              sx={{
                fontSize: "1.6rem",
                fontWeight: 800,
                color: modalTokens.textPrimary,
                lineHeight: 1.1,
              }}
            >
              Finance HQ
            </Typography>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {activeTab === "summary" && showCommissionProfitSection && (
            <Button
              variant="outlined"
              onClick={handleExportCommissionDetails}
              disabled={!invoiceNumber}
              startIcon={<FileDownloadOutlined fontSize="small" />}
              sx={commExportBtnSx}
              removeUpperCase
            >
              Export
            </Button>
          )}

          {activeTab === "summary" && canEditCommissionRate && (
            <Button
              variant="outlined"
              onClick={handleAuditLogClick}
              disabled={auditLogLoading || !invoiceNumber}
              startIcon={<History fontSize="small" />}
              sx={commViewChangesBtnSx}
              removeUpperCase
            >
              View Changes
            </Button>
          )}

          {activeTab === "summary" &&
            SalesDetailsInfo?.[0]?.invoiceId &&
            invoiceId == SalesDetailsInfo[0].invoiceId &&
            canViewExcelData && (
            <Button
              variant="outlined"
              sx={commExportBtnSx}
              onClick={showExcelPopupData}
              startIcon={
                <img
                  src={require("../../../assets/excel_icon.png")}
                  alt="Excel Icon"
                  style={{ width: 24, height: 24 }}
                />
              }
              removeUpperCase
            >
              View Excel Data
            </Button>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
        }}
      >
        <SectionTabNav
          tabs={detailTabs}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as "summary" | "invoice" | "order")}
        />
        {isPurchaseAndBill && <PurchaseAndBillIndicator />}
      </Box>

      {activeTab === "summary" && (
      <Box mt={2} pb={5}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Box>
              {/* <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: 3,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ sm: "flex-start" }}
                  spacing={2}
                  mb={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Payment Status Overview
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
                    >
                      Last Updated
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {lastUpdated.toLocaleString(undefined, {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={2}>
                  {tiles.map((t) => (
                    <Grid item xs={12} md={3}  key={t.key}>
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: stateMeta[t.state].bg,
                          borderColor: stateMeta[t.state].ring,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {t.label}
                            </Typography>
                            <StatusChip state={t.state}/>
                          </Box>

                          <StateIcon state={t.state} />
                        </Stack>

                        <Typography
                          mt={1.5}
                          color="text.primary"
                          fontWeight={600}
                        >
                          {t.state === "partial" && (
                            <>
                              {money(t.paid)}{" "}
                              <Typography
                                component="span"
                                color="text.secondary"
                                fontWeight={400}
                              >
                                / {money(t.total)}
                              </Typography>
                            </>
                          )}
                          {t.state === "paid" && t.paid === t.total && (
                            <>{money(t.total)}</>
                          )}
                          {t.state === "unpaid" && (
                            <>
                              {money(0)}{" "}
                              <Typography
                                component="span"
                                color="text.secondary"
                                fontWeight={400}
                              >
                                / {money(t.total)}
                              </Typography>
                            </>
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid> */}
              <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                  mt: 1,
                  px: 2,
                  py: 1.5,
                  backgroundColor: bgColor,
                  border: `1px solid ${colors.grey[700]}`,
                  borderRadius: 2,
                }}
              >
                <ReportGmailerrorred color={statusColor} />
                <Typography variant="body2" color="text.primary">
                  {statusStr}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={headerCardSx}>
              {/* Amount calculation only — Invoice/Order identity lives in the page tabs now. */}
              <Box
                sx={{
                  ...headerRowSx,
                  pb: headerExpanded ? 0 : headerRowSx.py,
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={() => setHeaderExpanded((prev) => !prev)}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHeaderExpanded((prev) => !prev);
                  }}
                  sx={{ position: "absolute", top: 10, right: 10 }}
                >
                  {headerExpanded ? <RemoveIcon /> : <AddIcon />}
                </IconButton>

                <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.5,
                      pr: 5,
                    }}
                  >
                    <Box>
                      <Typography variant="h5" fontWeight="bold" color="#22C55E">
                        Invoice Calculation
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {invoiceDate && (
                        <Box sx={headerMetaBadgeSx}>
                          <CalendarTodayOutlined sx={{ fontSize: "0.9rem", color: colors.grey[400] }} />
                          <Typography component="span" sx={headerMetaLabelSx}>
                            Invoice Date:{" "}
                            <Typography component="span" sx={headerMetaValueSx}>
                              {invoiceDate}
                            </Typography>
                          </Typography>
                        </Box>
                      )}
                      {invoiceStatus && (
                        <Box sx={headerMetaBadgeSx}>
                          <DescriptionOutlined sx={{ fontSize: "0.9rem", color: colors.grey[400] }} />
                          <Typography component="span" sx={headerMetaLabelSx}>
                            Status:{" "}
                            <Typography
                              component="span"
                              sx={{
                                ...headerMetaValueSx,
                                color: invoiceStatusDisplay.color,
                              }}
                            >
                              {invoiceStatusDisplay.label}
                            </Typography>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Collapsed hero: the two numbers users need most */}
                  {!headerExpanded && (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${colors.info[300]}55`,
                          backgroundColor:
                            theme.palette.mode === "dark" ? "rgba(56,189,248,0.06)" : "rgba(56,189,248,0.08)",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: colors.info[300], mb: 0.5 }}>
                          Service Fee Base
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.35rem",
                            fontWeight: 800,
                            color: colors.info[300],
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {priceFormatter(commissionBaseAmount)}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 2,
                          border: `1px solid #22c55e55`,
                          backgroundColor:
                            theme.palette.mode === "dark" ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.08)",
                        }}
                      >
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#22c55e", mb: 0.5 }}>
                          Invoice Total
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "1.35rem",
                            fontWeight: 800,
                            color: "#22c55e",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {priceFormatter(invoiceGrandTotal)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
              </Box>

              <Collapse in={headerExpanded}>
                <Box
                  sx={{
                    px: headerRowSx.px,
                    pt: 1.5,
                    pb: headerRowSx.py,
                  }}
                >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: 1.5,
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1.75,
                          borderRadius: 2,
                          border: `1px solid ${colors.info[300]}55`,
                          backgroundColor:
                            theme.palette.mode === "dark" ? "rgba(56,189,248,0.06)" : "rgba(56,189,248,0.08)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.85,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: colors.info[300], textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Service Fee Base
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor }}>
                            Invoice Amount:
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor, fontVariantNumeric: "tabular-nums" }}>
                            {priceFormatter(invoiceTotal)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor }}>
                            Discount:
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor, fontVariantNumeric: "tabular-nums" }}>
                            -{priceFormatter(discountAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor }}>
                            Credit Amount:
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor, fontVariantNumeric: "tabular-nums" }}>
                            -{priceFormatter(creditAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ mt: "auto" }}>
                          <Box sx={{ borderTop: `1px solid ${colors.info[300]}55`, mb: 0.85 }} />
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
                            <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: colors.info[300] }}>
                              Service Fee Base:
                            </Typography>
                            <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: colors.info[300], fontVariantNumeric: "tabular-nums" }}>
                              {priceFormatter(commissionBaseAmount)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          px: 2,
                          py: 1.75,
                          borderRadius: 2,
                          border: `1px solid #22c55e55`,
                          backgroundColor:
                            theme.palette.mode === "dark" ? "rgba(34,197,94,0.06)" : "rgba(34,197,94,0.08)",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.85,
                        }}
                      >
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Invoice Total
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor }}>
                            {/* From service fee base */}
                            {/* CS-2104 */}
                            From Service Fee Base
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor, fontVariantNumeric: "tabular-nums" }}>
                            {priceFormatter(commissionBaseAmount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor }}>
                            Shipping Charges:
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor, fontVariantNumeric: "tabular-nums" }}>
                            +{priceFormatter(shippingCharge)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor }}>
                            Late Fees:
                          </Typography>
                          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: lineTextColor, fontVariantNumeric: "tabular-nums" }}>
                            +{priceFormatter(lateFees)}
                          </Typography>
                        </Box>
                        <Box sx={{ mt: "auto" }}>
                          <Box sx={{ borderTop: `1px solid #22c55e55`, mb: 0.85 }} />
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 2 }}>
                            <Typography sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#22c55e" }}>
                              Invoice Total:
                            </Typography>
                            <Typography sx={{ fontSize: "1.2rem", fontWeight: 800, color: "#22c55e", fontVariantNumeric: "tabular-nums" }}>
                              {priceFormatter(invoiceGrandTotal)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                </Box>
              </Collapse>
            </Box>
          </Grid>

          {(invoiceCommissionSummary.v3Rows.length > 0 ||
            invoiceCommissionSummary.payableRows.length > 0 ||
            invoiceCommissionSummary.netRows.length > 0) && (
            <Grid item xs={12}>
              <Box sx={headerCardSx}>
                <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                  <InvoiceCommissionSummary
                    v3Rows={invoiceCommissionSummary.v3Rows}
                    payableRows={invoiceCommissionSummary.payableRows}
                    netRows={invoiceCommissionSummary.netRows}
                  />
                </Box>
              </Box>
            </Grid>
          )}
          {(invoiceAgencyList?.length ?? 0) > 0 && (
          <Grid item xs={12} md={12}>
            <Box
              sx={{
                py: 2,
                borderRadius: 3,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#03112146" : "#e4e4e454",
                border: `1px solid #6d6d6d46`,
              }}
            >
              <Box p={2}>
                <Typography variant="h5" fontWeight="bold" color="#22C55E">
                  Service Fee Calculation
                </Typography>
              </Box>
              <Box sx={{ pl: 1, pb: 1, pr: 1 }}>
                {paymentsWithShippingAdjustment.length === 0 ? (
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: "center",
                      color: "text.secondary",
                      py: 4,
                    }}
                  >
                    No payments found
                  </Typography>
                ) : (
                  paymentsWithShippingAdjustment.map((payment: any, index: any) => {
                    const isExpanded = expandedPanels.includes(payment.id);
                    const isDisabled = payment.adjustedAmount === 0;
                    return (
                      <Accordion
                        key={payment.id}
                        expanded={isExpanded}
                        onChange={isDisabled ? undefined : handleAccordionToggle(payment.id)}
                        disableGutters
                        elevation={0}
                        square
                        sx={{
                          backgroundColor: colors.grey[800],
                          borderRadius: 2,
                          mb: 1,
                          p: 0,
                          "&:before": { display: "none" },
                          // transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: colors.grey[700],
                            // transform: isDisabled ? "none" : "translateX(4px)",
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={isDisabled ? null : (isExpanded ? <RemoveIcon /> : <AddIcon />)}
                          sx={{
                            backgroundColor: "transparent",
                            borderRadius: 2,
                            px: { xs: 1.5, sm: 2.5 },
                            py: 0.5,
                            minHeight: "48px",
                            "&.Mui-expanded": {
                              minHeight: "48px",
                            },
                            "& .MuiAccordionSummary-content": {
                              my: 0.5,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", md: "row" },
                              alignItems: { xs: "flex-start", md: "center" },
                              width: "100%",
                              gap: { xs: 1, md: 1.5 }
                            }}
                          >
                            {/* Payment Number */}
                            <Box sx={{ minWidth: { xs: "auto", md: "220px" } }}>
                              <Typography
                              variant="h6"
                                sx={{
                                  color: colors.success[300]
                                }}
                              >
                                INVOICE PAYMENT #{index + 1}
                              </Typography>
                            </Box>

                            {/* Amount and Shipping Info Container */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: { xs: 0.5, sm: 1.5 },
                                flex: 1,
                                width: { xs: "100%", sm: "auto" }
                              }}
                            >
                              {/* Payment Amount */}
                              <Box sx={{ minWidth: { xs: "auto", md: "100px" }, textAlign: "right" }}>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    ...(payment.paymentStatus === PAYMENT_STATUS.VOID ? {
                                      textDecoration: "line-through red",
                                      textDecorationThickness: "2px",
                                    } : {}),
                                    color: payment.paymentStatus === PAYMENT_STATUS.VOID ? "text.disabled" : "text.primary",
                                    fontVariantNumeric: "tabular-nums",
                                  }}
                                >
                                  ${payment.amount.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </Typography>
                              </Box>

                              {/* Combined Breakdown - Only show if there are deductions */}
                              {(payment.hasShippingDeduction || payment.hasLateFeeDeduction) && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    gap: 0.5,
                                    fontSize: { xs: "0.813rem", sm: "0.875rem" }
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: "text.primary",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    (
                                  </Typography>

                                  {/* Adjusted Amount */}
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      ...(payment.paymentStatus === PAYMENT_STATUS.VOID ? {
                                        textDecoration: "line-through red",
                                        textDecorationThickness: "2px",
                                      } : {}),
                                      color: payment.paymentStatus === PAYMENT_STATUS.VOID ? "text.disabled" : "text.primary",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    ${payment.adjustedAmount.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                  </Typography>

                                  {/* Shipping Charge */}
                                  {shippingCharge > 0 && payment.hasShippingDeduction && (
                                    <>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: colors.success[400]
                                        }}
                                      >
                                        +
                                      </Typography>
                                      <Typography
                                        sx={{
                                          color: "text.secondary",
                                          fontSize: { xs: "0.813rem", sm: "0.875rem" },
                                          whiteSpace: { xs: "normal", sm: "nowrap" },
                                        }}
                                      >
                                        Shipping
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: "text.primary",
                                          fontVariantNumeric: "tabular-nums",
                                        }}
                                      >
                                        ${payment.shippingChargeDeducted.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Typography>
                                    </>
                                  )}

                                  {/* Late Fees */}
                                  {lateFees > 0 && payment.hasLateFeeDeduction && (
                                    <>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: colors.success[400]
                                        }}
                                      >
                                        +
                                      </Typography>
                                      <Typography
                                        sx={{
                                          color: "text.secondary",
                                          fontSize: { xs: "0.813rem", sm: "0.875rem" },
                                          whiteSpace: { xs: "normal", sm: "nowrap" },
                                        }}
                                      >
                                        Late Fees
                                      </Typography>
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          color: "text.primary",
                                          fontVariantNumeric: "tabular-nums",
                                        }}
                                      >
                                        ${payment.lateFeeDeducted.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}
                                      </Typography>
                                    </>
                                  )}
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      color: "text.primary",
                                      fontVariantNumeric: "tabular-nums",
                                    }}
                                  >
                                    )
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Date */}
                            <Box
                              sx={{
                                ml: { xs: 0, md: "auto" },
                                mr: { xs: 0, md: 2 },
                                mt: { xs: 0.5, md: 0 }
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "text.disabled",
                                  fontSize: { xs: "0.75rem", sm: "0.813rem" },
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ({payment.paymentDate.toLocaleString()})
                              </Typography>
                            </Box>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ p: 0 }}>
                          <CustomMultipleTable
                            sections={[
                              ...(invoiceCommissionSummary.v3Rows.length > 0
                                ? [{
                                columns: receivedColumn,
                                isStrikeThrough: payment.paymentStatus === PAYMENT_STATUS.VOID,
                                // Display-only NA row when this payment has no V3 commission level
                                rows: [
                                  ...(payment.levels.some((lvl: any) => isV3Type(lvl.level))
                                    ? []
                                    : [{
                                      invoiceLevel: commissionTypeLabel(COMMISSION_TYPE_V3_KEY),
                                      commission: "-",
                                      expected: "-",
                                      expectedDate: "-",
                                      received: "-",
                                      receivedDate: "-",
                                      action: null,
                                    }]),
                                  ...payment.levels
                                  .filter((lvl: any) => isV3Type(lvl.level))
                                  .map((lvl: any) => ({
                                    invoiceLevel: commissionTypeLabel(lvl.level),
                                    commission: lvl.commission,
                                    expected: `$${lvl.expected.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`,
                                    received: `$${lvl.paid.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`,
                                    expectedDate: lvl.expectedDate,
                                    receivedDate: (
                                      <Box
                                        component="span"
                                        sx={{
                                          cursor: "pointer",
                                          color: colors.info[300],
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDateClick({
                                            date: lvl.commissionReceivedDate,
                                            paymentId: payment.id,
                                            level: lvl.level,
                                            invoiceId: lvl.invoiceId,
                                            agencyId: lvl.agencyId,
                                            dateType: "received",
                                          });
                                        }}
                                      >
                                        {lvl.commissionReceivedDate}
                                      </Box>
                                    ),
                                    action: payment.paymentStatus !== PAYMENT_STATUS.VOID && renderActionButtons({
                                      paymentId: payment.id,
                                      level: lvl.level,
                                      received: lvl.paid,
                                      expected: lvl.expected,
                                      invoiceId: lvl.invoiceId,
                                      agencyId: lvl.agencyId,
                                      commissionRate: parseFloat(lvl.commission.replace('%', '')),
                                      invoiceAgencyId: invoiceAgencyList?.find(a => a.agencyId === lvl.agencyId)?.invoiceAgencyId,
                                    }),
                                  })),
                                ],
                              }]
                                : []),
                              ...(invoiceCommissionSummary.payableRows.length > 0
                                ? [{
                                columns: paidColumn,
                                isStrikeThrough: payment.paymentStatus === PAYMENT_STATUS.VOID,
                                // Display-only NA row when this payment has no AGENCY commission level
                                rows: [
                                  ...(payment.levels.some((lvl: any) => isAgencyType(lvl.level))
                                    ? []
                                    : [{
                                      invoiceLevel: commissionTypeLabel(COMMISSION_TYPE_AGENCY_KEY),
                                      commission: "-",
                                      expected: "-",
                                      expectedDate: "-",
                                      paid: "-",
                                      paidDate: "-",
                                      action: null,
                                    }]),
                                  ...payment.levels
                                  .filter((lvl: any) => !isV3Type(lvl.level))
                                  .map((lvl: any) => ({
                                    invoiceLevel: commissionTypeLabel(lvl.level) + " (" + lvl.agencyName + ")",
                                    commission: lvl.commission,
                                    expected: `$${lvl.expected.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`,
                                    paid: `$${lvl.paid.toLocaleString(
                                      undefined,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`,
                                    expectedDate: lvl.expectedDate,
                                    paidDate: (
                                      <Box
                                        component="span"
                                        sx={{
                                          cursor: "pointer",
                                          color: colors.info[300],
                                          "&:hover": { textDecoration: "underline" },
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDateClick({
                                            date: lvl.commissionReceivedDate,
                                            paymentId: payment.id,
                                            level: lvl.level,
                                            invoiceId: lvl.invoiceId,
                                            agencyId: lvl.agencyId,
                                            dateType: "paid",
                                          });
                                        }}
                                      >
                                        {lvl.commissionReceivedDate}
                                      </Box>
                                    ),
                                    action: payment.paymentStatus !== PAYMENT_STATUS.VOID && renderActionButtons({
                                      paymentId: payment.id,
                                      level: lvl.level,
                                      paid: lvl.paid,
                                      expected: lvl.expected,
                                      invoiceId: lvl.invoiceId,
                                      agencyId: lvl.agencyId,
                                      commissionRate: parseFloat(lvl.commission.replace('%', '')),
                                      invoiceAgencyId: invoiceAgencyList?.find(a => a.agencyId === lvl.agencyId)?.invoiceAgencyId,
                                    }),
                                  })),
                                ],
                              }]
                                : []),
                            ]}
                          />
                        </AccordionDetails>
                      </Accordion>
                    );
                  })
                )}
              </Box>
            </Box>
          </Grid>
          )}
          <CommissionProfitSection
            invoiceId={parseInt(invoiceId)}
            profitDetail={commissionProfitDetail}
            visible={!!invoiceDetail}
            purchaseAndBill={isPurchaseAndBill}
            mfgInvoiceAmount={invoiceGrandTotal}
            v3ServiceFeeTotal={nonPbServiceFees.v3ServiceFeeTotal}
            agencyServiceFeeTotal={nonPbServiceFees.agencyServiceFeeTotal}
            overrideServiceFeeTotal={nonPbServiceFees.overrideServiceFeeTotal}
          />
        </Grid>
      </Box>
      )}

      {activeTab === "invoice" && (
        <Box mt={2} pb={5}>
          {invoiceId ? (
            <BillingDetail
              key={`finance-hq-invoice-${invoiceId}`}
              embedded
              invoiceIdOverride={Number(invoiceId)}
            />
          ) : (
            <Box sx={{ ...headerCardSx, p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Invoice details are unavailable for this record.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {activeTab === "order" && (
        <Box mt={2} pb={5}>
          {orderId ? (
            <OrderDetails
              key={`finance-hq-order-${orderId}`}
              embedded
              orderIdOverride={orderId}
            />
          ) : (
            <Box sx={{ ...headerCardSx, p: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Order details are unavailable for this record.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <ExcelDetailsPopup
        onClose={() => setShowExcelPopup(false)}
        open={showExcelPopup}
        rows={SalesDetailsInfo}
      />
      <CommissionPayoutPopup
        open={showCommissionPayoutPopup.show}
        onClose={() =>
          setShowCommissionPayoutPopup({
            commissionObject: initializeAddCommissionRequest(),
            title: "",
            label: "",
            show: false,
            expectedAmount: undefined,
            alreadyPaidAmount: undefined,
          })
        }
        onConfirm={(data) => {
          handleConfirmCommissionPayout(data);
        }}
        commissionObject={showCommissionPayoutPopup.commissionObject}
        currency="$"
        title={showCommissionPayoutPopup.title}
        commissionAmountLabel={showCommissionPayoutPopup.label}
        expectedAmount={showCommissionPayoutPopup.expectedAmount}
        alreadyPaidAmount={showCommissionPayoutPopup.alreadyPaidAmount}
      />
      {showInfoPopup.show && (
        <InfoPopup
          open={showInfoPopup.show}
          onClose={() => {
            setShowInfoPopup({ message: "", title: "", show: false });
            setPendingCommissionData(null);
          }}
          content={showInfoPopup.message}
          severity="warning"
          title={showInfoPopup.title}
          confirmButton={"Confirm"}
          onConfirm={() => {
            setShowInfoPopup({ message: "", title: "", show: false });

            if (pendingCommissionData) {
              openCommissionPayoutPopup(
                pendingCommissionData.level,
                pendingCommissionData.paymentId,
                pendingCommissionData.invoiceId,
                pendingCommissionData.agencyId,
                pendingCommissionData.expected,
                pendingCommissionData.alreadyPaid
              );
              setPendingCommissionData(null);
            }
          }}
        />
      )}
      <DateUpdatePopup
        open={showDateUpdatePopup.show}
        onClose={() => setShowDateUpdatePopup({ show: false, data: null })}
        onConfirm={(updatedRequest) => {
          handleUpdateCommissionDate(updatedRequest)
        }}
        requestData={showDateUpdatePopup.data}
        title={
          showDateUpdatePopup.data?.dateType
            ? `Update ${showDateUpdatePopup.data.dateType.charAt(0).toUpperCase()}${showDateUpdatePopup.data.dateType.slice(1)} Date`
            : ""
        }
      />
      <EditCommissionPopup
        open={showEditCommissionPopup.show}
        handleClose={() => setShowEditCommissionPopup({ show: false, paymentData: null, agencyData: null })}
        mode="commission-details"
        paymentData={showEditCommissionPopup.paymentData}
        agencyData={showEditCommissionPopup.agencyData}
        onCommissionRateUpdate={handleCommissionRateUpdate}
      />
      <CustomAlert
        open={showNoChangeAlert}
        onClose={() => setShowNoChangeAlert(false)}
        severity="warning"
        message="No changes were made to the service rate."
        alertTitle="No Changes"
        autoHideDuration={5000}
      />
      <AuditLogDisplayPopup
        open={showAuditLogPopup}
        onClose={handleCloseAuditLog}
        title={`Audit Trail #${invoiceNumber}`}
        auditLogData={auditLogData}
      />
    </Box>
  );
};

export default CommissionDetails;
